import React, {useEffect} from "react";
import {useFormContext, UseFormSetValue} from "react-hook-form";
import {DeliveryArea, DeliveryAreaFormValues, deliveryAreaFormValuesFormValidation} from "@/data/entities/deliveryArea";
import {Icon, InputLabel, TextField} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {getInputCurrencyProps, getInputValidationProps} from "@/data/Validation";
import TextAddOn from "peggirkit/dist/components/Input/Field/TextField/TextFieldAddOn/TextAddOn";
import IconAddOn from "peggirkit/dist/components/Input/Field/TextField/TextFieldAddOn/IconAddOn";
import {ClockIcon} from "@heroicons/react/24/outline";


const setValues = (
    featuredProduct: DeliveryArea | null,
    setValue: UseFormSetValue<DeliveryAreaFormValues>,
) => {
    if (!featuredProduct) {
        return;
    }

    setValue("postalCode", featuredProduct.postalCode)
    setValue("deliveryTimeMinutes", featuredProduct.deliveryTimeMinutes)
    setValue("minimumOrderAmount", featuredProduct.minimumOrderAmount + "")
    setValue("deliveryFee", featuredProduct.deliveryFee + "")
};

type Props = {
    deliveryArea: DeliveryArea | null,
    handleSubmitHandler: () => void,
    disabledState: boolean,
};

const EditDeliveryAreaForm = ({deliveryArea, handleSubmitHandler, disabledState}: Props) => {
    const intl = useIntl();
    const {formState: {errors}, register, setValue, reset} = useFormContext<DeliveryAreaFormValues>();

    // Set values after loading existing delivery area
    useEffect(() => {
        reset();
        setValues(deliveryArea, setValue);
    }, [deliveryArea, setValue, reset]);

    return (
        <form
            className={"w-full flex flex-col gap-5 md:gap-8 my-6 md:my-8"}
            onSubmit={handleSubmitHandler}
        >
            {
                deliveryArea &&
                <TextField
                    type={"number"}
                    label={<InputLabel><FormattedMessage id={"id"}/></InputLabel>}
                    tip={<FormattedMessage id={"internalIdReferencePurposes"}/>}
                    defaultValue={deliveryArea.id}
                    disabled={true}
                />
            }

            <TextField
                type={"text"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"postalCode"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "deliveryAreaPostalCodeTip"})}
                {...register("postalCode", {
                    ...getInputValidationProps(deliveryAreaFormValuesFormValidation.postalCode, intl),
                    pattern: {
                        value: /^\d{4}$/,
                        message: intl.formatMessage({id: "enterValidPostalCode"})
                    },
                })}
                placeholder={"6800"}
                disabled={disabledState}
                error={errors.postalCode && errors.postalCode.message}
            />

            <TextField
                type={"number"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"travelTimeMinutes"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "travelTimeMinutesTip"})}
                {...register("deliveryTimeMinutes", {
                    ...getInputValidationProps(deliveryAreaFormValuesFormValidation.deliveryTimeMinutes, intl),
                    valueAsNumber: true,
                })}
                placeholder={"15"}
                leading={IconAddOn({icon: <Icon icon={ClockIcon}/>})}
                disabled={disabledState}
                error={errors.deliveryTimeMinutes && errors.deliveryTimeMinutes.message}
            />

            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"minimumOrderAmount"}/>
                    </InputLabel>
                }
                {...register("minimumOrderAmount", {
                    ...getInputValidationProps(deliveryAreaFormValuesFormValidation.minimumOrderAmount, intl),
                    ...getInputCurrencyProps(intl),
                })}
                leading={TextAddOn({text: "€"})}
                placeholder={"20,00"}
                disabled={disabledState}
                error={errors.minimumOrderAmount && errors.minimumOrderAmount.message}
            />

            <TextField
                type={"text"}
                inputMode={"decimal"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"deliveryFee"}/>
                    </InputLabel>
                }
                {...register("deliveryFee", {
                    ...getInputValidationProps(deliveryAreaFormValuesFormValidation.deliveryFee, intl),
                    ...getInputCurrencyProps(intl),
                })}
                leading={TextAddOn({text: "€"})}
                placeholder={"3,50"}
                disabled={disabledState}
                error={errors.deliveryFee && errors.deliveryFee.message}
            />
        </form>
    );
};

export default EditDeliveryAreaForm;